/**
 * The STYLE-TABLE + LAYOUT-PLOT-SETTINGS action family (editorStore.ts slice).
 *
 * The family = the four style tables (dimension / multileader / text styles +
 * layers) + the per-layout plot settings. Twelve store actions:
 *   - direct-set → dispatch (Lane J D3-1): `updateDimensionStyle`,
 *     `addDimensionStyle`, `updateMultileaderStyle`, `addMultileaderStyle`,
 *     `updateTextStyle`, `addLayer`, `updateLayer`, `setLayoutPlotSettings`;
 *   - already-dispatch siblings: `deleteDimensionStyle`, `renameDimensionStyle`,
 *     `deleteMultileaderStyle`, `deleteLayer`.
 *
 * ## Lane J (D3-1) — the direct-set → dispatch conversion (undo-able)
 *
 * BEFORE Lane J the eight `add*` / `update*` / `setLayoutPlotSettings` actions
 * poked `geometryDocument.styleTables` / `.layouts` STRAIGHT via zustand `set`
 * (an O(1) style-table edit that skipped the entity rebuild), BYPASSING the
 * universal dispatch chokepoint → `computeInverse` → `history.past`. So a layer
 * colour / DIMSTYLE / MLEADERSTYLE / text-style / page-setup edit was NOT
 * undo-able — Ctrl+Z did not see it (the main live P2 functional defect). To
 * bump the agent-visible `tableRevision` those direct-set actions maintained a
 * whole private bump-accumulator machinery (`directSetTableBumpSlice`,
 * `pendingDirectSetBumpClasses`, `TABLE_ONLY_BUMP`).
 *
 * Lane J routes them through `get().dispatch({ type: "header/…" })`. The
 * mutation now flows through the SAME chokepoint the delete/rename siblings
 * already used (`resolveGeometryDocumentCommand` → `reduceGeometryDocument` →
 * `applyStyleTablesActionWithMutations` in `styleTablesReducer.ts`), so it gets:
 *   - a history entry + a snapshot undo inverse
 *     (`header/restore-style-tables-snapshot`, hand-built in editorStore.ts);
 *   - the standard chokepoint DUAL-BUMP (`tableChanged` → `tableRevision`),
 *     which is why the private direct-set bump machinery is GONE.
 * This obeys the CLAUDE.md rule "любые изменения геометрии только через Command
 * Engine" — the direct-set path was the last violator in this family.
 *
 * These wrappers stay the SINGLE entry point for their public signatures (UI
 * dialogs / hooks / window API / tests keep calling `store.updateLayer(…)` etc.
 * unchanged): they own the VALIDATION (empty/dupe-name rejection, base-style
 * resolution, SL-4/SL-5 imported-handle stripping, plot-settings no-op guard)
 * and construct the FULLY-RESOLVED record BEFORE dispatch so REDO replays a
 * deterministic command. The reducer does the actual immutable mutation.
 *
 * `editorStore.ts` composes the slice into its `create()` initializer via
 * `...createStyleTableActionsSlice(get)` — `get` IS the store's `getState`.
 */
import {
  type DimensionStyle,
  type DrawingDocument,
  type LayerDefinition,
  type LayoutPlotSettings,
  type MultileaderStyle,
  type TextStyle,
} from "@dxf-ai-editor/drawing-model";
import {
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
} from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import { namesEqualCaseInsensitive } from "./editorStoreInitialState";
import {
  isStyleTableUpdateNoOp,
  resolveLayoutPlotSettingsWrite,
} from "./styleTablesReducer";
// GitHub Codex review (PR #534, round 2) P2 — `deleteLayer`/`renameLayer`
// dispatch, then need to know whether that dispatch was rejected by the
// fail-closed collab-lock EDIT veto (`editorStore.ts`'s `dispatch()`), so
// they can report `{ ok: false, error }` instead of blindly `{ ok: true }`.
// Lives in its own leaf module (not imported straight from `editorStore.ts`)
// specifically to avoid a two-file import cycle — see that module's header.
import { wasLastDispatchCollabLockVetoed } from "./collabLockDispatchVetoSignal";
// T7 (agent-bridge), local Codex round-16 P2 — reused (not re-derived) by
// `stampCurrentLayerOntoCreateCommand` below; see that function's doc
// comment for why CLAYER shares CECOLOR's stampable-create membership.
import { CE_COLOR_STAMPABLE_CREATE } from "./cecolorStore";

/**
 * The store surface this slice reads — a STRUCTURAL SUBSET of `EditorStore`
 * (editorStore.ts). Member types must stay IDENTICAL to the host store's
 * declarations so `EditorStore` stays assignable to this type for the `get`
 * parameter. Declared here (not imported from editorStore.ts) so the module has
 * NO back-edge into `editorStore.ts` — the deps graph stays acyclic.
 */
interface StyleTablesHostStore {
  state: EditorShellState;
  geometryDocument: DrawingDocument;
  dispatch: (command: EditorCommand | DynamicBlockCommand) => void;
}

/** The zustand `getState` of the host store. */
type StyleTablesGet = () => StyleTablesHostStore;

/**
 * The action surface this slice contributes to `EditorStore`. `editorStore.ts`
 * intersects this into its `EditorStore` type, so external importers of
 * `EditorStore` / `useEditorStore` see the exact same action signatures as
 * before.
 */
export interface StyleTableActionsSlice {
  updateDimensionStyle: (
    styleName: string,
    updates: Partial<DimensionStyle>,
  ) => void;
  /**
   * Create a new dimension style, copying all DIMVARs from an existing
   * "base" style (default "Standard"). Returns `{ ok: false, error }` if
   * `name` is empty or already used. Mirrors AutoCAD `DIMSTYLE New`.
   */
  addDimensionStyle: (
    name: string,
    basedOn?: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Delete a dimension style by name. "Standard" cannot be deleted (mirrors
   * AutoCAD). Any dimensions referencing the deleted style fall back to
   * "Standard" automatically.
   */
  deleteDimensionStyle: (
    name: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Rename a dimension style. "Standard" cannot be renamed. Updates all
   * dimension entities that reference the old name.
   */
  renameDimensionStyle: (
    oldName: string,
    newName: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Set the CURRENT dimension style (AutoCAD `DIMSTYLE` sysvar / the Dimension
   * Style Manager "Set Current" button). The current style is the one new
   * DIMENSION entities inherit; it is persisted as the default object property
   * `"Dimension style"` in `state.propertyWorkflows.defaultObjectProperties`
   * (the same current-object-property slot CLAYER / current colour use). Routed
   * through the `properties/apply` command with an EMPTY `entityIds` list — the
   * property reducer's no-selection branch merges `values` into the default
   * object properties and pushes an audit event — so the change flows through the
   * Command Engine chokepoint. Like CLAYER / the current colour it is a
   * current-POINTER change, NOT a geometry/table edit: `computeInverse` has no
   * inverse for a no-selection `properties/apply` (returns null) and the geometry
   * document is untouched, so it is NOT placed on the undo stack and does NOT
   * clear the redo stack — matching AutoCAD, where changing the DIMSTYLE sysvar
   * is not an undoable operation. Returns `{ ok: false, error }` when `name` does
   * not name an existing style.
   */
  setCurrentDimensionStyle: (
    name: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Mirror of `updateDimensionStyle` for MLEADERSTYLE. Updates the named
   * style in `geometryDocument.styleTables.multileaderStyles[]` and
   * triggers `enrichMultileaderEntities` so the renderer picks up the
   * change immediately. No-op if `styleName` does not exist.
   */
  updateMultileaderStyle: (
    styleName: string,
    updates: Partial<MultileaderStyle>,
  ) => void;
  /**
   * Create a new MLEADERSTYLE entry by copying all fields from an
   * existing base style (default "Standard"). Returns `{ ok: false }`
   * if the name is empty or already in use. Mirrors AutoCAD
   * `MLEADERSTYLE New`.
   */
  addMultileaderStyle: (
    name: string,
    basedOn?: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Delete a MLEADERSTYLE entry. Refuses to delete "Standard" (matching
   * AutoCAD). Any multileader entities still referencing the deleted
   * style fall back to "Standard" automatically.
   */
  deleteMultileaderStyle: (
    name: string,
  ) => { ok: true } | { ok: false; error: string };
  updateTextStyle: (styleName: string, updates: Partial<TextStyle>) => void;
  /**
   * Add a new layer to `geometryDocument.styleTables.layers`. Rejects
   * empty names and duplicates (case-sensitive). Mirrors AutoCAD LAYER New.
   */
  addLayer: (name: string) => { ok: true } | { ok: false; error: string };
  /**
   * Update layer properties (color, visible, frozen, locked, etc.).
   * No-op if `name` does not exist in the layer table.
   */
  updateLayer: (name: string, updates: Partial<LayerDefinition>) => void;
  /**
   * Delete a layer by name. Layer "0" cannot be deleted (mirrors AutoCAD).
   * Entities on the deleted layer are reassigned to layer "0".
   */
  deleteLayer: (name: string) => { ok: true } | { ok: false; error: string };
  /**
   * T7 (agent-bridge layer-mgmt) — rename a layer, cascading to every entity
   * currently on it (mirrors `renameDimensionStyle`, NOT `deleteLayer`'s
   * reassign-to-"0": a rename keeps every entity "on" the SAME (renamed)
   * layer). Layer "0" cannot be renamed (mirrors AutoCAD, and mirrors
   * "Standard" for DIMSTYLE). Rejects an empty/duplicate `newName` and a
   * nonexistent `oldName`. A self-rename (`oldName === newName` after trim)
   * is a true no-op. If `oldName` was the CURRENT layer (CLAYER), the
   * chokepoint reconcile (`reconcileCurrentLayerAgainstTable`, called
   * unconditionally after every dispatch) CARRIES CLAYER FORWARD to
   * `newName` (local Codex round-5 P2 — mirrors `renameDimensionStyle`'s
   * current-pointer carry exactly: a rename is not a removal, so the renamed
   * layer stays current under its new name, matching AutoCAD). The SAME
   * reconcile still falls CLAYER back to "0" for every OTHER stranding class
   * (delete / undo / redo shrinking the table) — the rename branch is
   * evaluated first.
   */
  renameLayer: (
    oldName: string,
    newName: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * Set the CURRENT layer (AutoCAD CLAYER sysvar) — the layer new entities
   * inherit. Persisted as the default object property `"Layer"` in
   * `state.propertyWorkflows.defaultObjectProperties` (the same
   * current-object-property slot the current colour / current DIMSTYLE use).
   * Routed through the `properties/apply` command with an EMPTY `entityIds`
   * list — the property reducer's no-selection branch merges `values` into
   * the default object properties and pushes an audit event — so the change
   * flows through the Command Engine chokepoint (mirrors
   * `setCurrentDimensionStyle` exactly; see its doc comment for the full
   * rationale). Like CLAYER / CECOLOR / DIMSTYLE it is a current-POINTER
   * change, NOT a geometry/table edit: `computeInverse` has no inverse for a
   * no-selection `properties/apply` (returns null) and the geometry document
   * is untouched, so it is NOT placed on the undo stack and does NOT clear
   * the redo stack — matching AutoCAD, where changing CLAYER is not an
   * undoable operation. Returns `{ ok: false, error }` when `name` does not
   * name an existing layer (mirrors `setCurrentDimensionStyle`'s existence
   * check); the quickbar `<select>` can only ever offer a live name, but the
   * AI safe contour's SET_ACTIVE_LAYER dispatches `properties/apply` DIRECTLY
   * (bypassing this wrapper, like every AI action), so this validation only
   * protects the human-facing entry point — see `layerTranslators.ts` for why
   * the AI path is intentionally thin/total (mirrors `translateSetCannoscale`).
   */
  setCurrentLayer: (
    name: string,
  ) => { ok: true } | { ok: false; error: string };
  /**
   * PDF-6.6 — persist the per-layout PLOTSETTINGS for a paper-space layout.
   *
   * Plot settings are layout METADATA (paper size / margins / plot-area type /
   * window / scale / rotation), not geometry — but the update still flows
   * through the store's dispatch chokepoint (Lane J): the reducer rebuilds
   * `geometryDocument` via `assembleDrawingDocument` and writes the new
   * `plotSettings` into BOTH the by-space-id map (`layoutsBySpaceId[spaceId]`,
   * the exporter's primary key) AND the authoritative by-name dict
   * (`layouts[tabName]`) so they stay in lockstep (the merge paths rebuild
   * `layoutsBySpaceId` from `layouts`). The vector PDF exporter
   * (`resolvePlotSettings`, PDF-6.1) then reads the persisted page on the next
   * plot, so each layout remembers its own plot area / window / scale.
   *
   * No-op (does NOT dispatch, preserving the document reference) when `spaceId`
   * resolves to no layout entry (e.g. the MODEL tab, which has no LAYOUT object)
   * or when the settings already match — model-space plotting keeps the
   * transient defaults.
   */
  setLayoutPlotSettings: (
    spaceId: string,
    settings: LayoutPlotSettings,
  ) => void;
}

/**
 * Resolve the base record an `add<Style>` clones from: the requested
 * `basedOn` name, else "Standard", else the first record (the shared
 * find-chain of `addDimensionStyle` / `addMultileaderStyle`).
 */
function resolveBaseStyleEntry<T extends { name: string }>(
  currentStyles: readonly T[],
  basedOn: string,
): T | undefined {
  return (
    currentStyles.find((s) => s.name === basedOn) ??
    currentStyles.find((s) => s.name === "Standard") ??
    currentStyles[0]
  );
}

/**
 * Resolve `name` to the CANONICAL dimension-style name in `doc`, matching
 * CASE-INSENSITIVELY (returns `undefined` when no style matches). DIMSTYLE table
 * names — like every DXF symbol-table name — are case-insensitive (the
 * reference-heal suite treats imported `STANDARD`/mixed-case DIMSTYLE names as
 * equivalent), so `DIMSTYLE SET standard` / an API call with foreign casing must
 * resolve an imported `Standard`/`STANDARD` record instead of rejecting. The
 * returned CANONICAL name (the exact spelling stored in the table) is what
 * `setCurrentDimensionStyle` persists into the pointer, so the manager marker +
 * the new-dimension stamp + the entity remap (all exact-name compares) stay in
 * lockstep with the table. Takes the document as an ARGUMENT (the caller passes
 * `get().geometryDocument`) so it is NOT a read-through capture site (see
 * `spaceReadThroughCaptureSites` — an argument read is not a
 * `const X = …geometryDocument` binding).
 */
function resolveDimensionStyleName(
  doc: DrawingDocument,
  name: string,
): string | undefined {
  const lowered = name.toLowerCase();
  return (doc.styleTables?.dimensionStyles ?? []).find(
    (s) => s.name.toLowerCase() === lowered,
  )?.name;
}

/**
 * @codex P1 — stamp the CURRENT DIMSTYLE onto a `geometry/create-dimension`
 * command at the SINGLE dispatch chokepoint (mirrors `stampCeColorOntoCreate
 * Command`). New dimensions must inherit the current dimension style (AutoCAD
 * DIMSTYLE sysvar, persisted as `defaultObjectProperties["Dimension style"]`)
 * — before this the reducer always used `dimensionStyles[0]`, so "Set Current"
 * / `DIMSTYLE SET <name>` had no effect on newly-created dimensions.
 *
 * Returns the command UNCHANGED only when it is not a create-dimension or when
 * the caller already supplied an explicit `styleName` (a programmatic / AI caller
 * that named a style keeps it). Otherwise it stamps the CONCRETE style name the
 * reducer would resolve RIGHT NOW — the current DIMSTYLE (resolved
 * case-insensitively to its canonical table spelling) when set, else the
 * reducer's first-entry fallback (`dimensionStyles[0]`), else "Standard".
 *
 * @codex P2 — recording the concrete fallback (not leaving `styleName` absent
 * when unset) is what makes REDO deterministic: `setCurrentDimensionStyle`
 * intentionally PRESERVES the redo stack, so a user can create a dimension while
 * unset → undo → set a different current style → redo. If the recorded forward
 * omitted `styleName`, that redo would re-stamp the NOW-current style and
 * recreate a DIFFERENT dimension than the original. Stamping the resolved name up
 * front pins the created style to what it was at creation time, independent of
 * later "Set Current" changes (mirrors the CECOLOR stamped-forward precedent).
 *
 * Driven from `editorStore.dispatch` so the interactive draft, the command-line,
 * and the AI dimension paths ALL inherit the current DIMSTYLE through one seam.
 */
export function stampCurrentDimStyleOntoCreateCommand(
  command: EditorCommand,
  currentDimStyleName: string | undefined,
  doc: DrawingDocument,
): EditorCommand {
  if (command.type !== "geometry/create-dimension") return command;
  if ((command as { styleName?: unknown }).styleName !== undefined)
    return command;
  const dimStyles = doc.styleTables?.dimensionStyles ?? [];
  const resolved =
    (currentDimStyleName
      ? resolveDimensionStyleName(doc, currentDimStyleName)
      : undefined) ??
    dimStyles[0]?.name ??
    "Standard";
  return { ...command, styleName: resolved } as EditorCommand;
}

/**
 * T7 (agent-bridge), local Codex round-16 P2 (github PR #527 review) — the
 * stampable-create membership for CLAYER. Starts from `CE_COLOR_STAMPABLE_
 * CREATE` (cecolorStore.ts) — AutoCAD's "current object properties" (CECOLOR,
 * CLAYER, CELTYPE, …) share the SAME "inherited by every drawn entity on
 * creation" applicability, so most of that exhaustive create-* audit (and its
 * documented exclusions: block-definition container, array-clone-inherits-
 * source, dynamic-block metadata nodes) governs layer too — one shared core,
 * no independent drift for the members that DO coincide.
 *
 * Round-17 P2 (github PR #527 review, follow-up) — `geometry/create-viewport`
 * is ADDED here despite being EXCLUDED from `CE_COLOR_STAMPABLE_CREATE`: that
 * exclusion is specifically about the VP-FRAME's border COLOUR being a
 * separate render-gated concern (deferred to a future Category-C PR, per
 * `CE_COLOR_STAMPABLE_CREATE`'s own module comment) — it does NOT apply to
 * plain layer-FIELD assignment, which carries no rendering complexity. A
 * newly-created MVIEW viewport entity follows CLAYER exactly like every other
 * entity in AutoCAD, so leaving it out (as an unreviewed reuse of the CECOLOR
 * set would have) silently stranded MVIEW on layer "0" after SET_ACTIVE_LAYER
 * — exactly the class of bug this whole stamp exists to prevent.
 */
export const CURRENT_LAYER_STAMPABLE_CREATE: ReadonlySet<EditorCommand["type"]> =
  new Set<EditorCommand["type"]>([
    ...CE_COLOR_STAMPABLE_CREATE,
    "geometry/create-viewport",
  ]);

/**
 * Stamp CLAYER (the current-layer pointer, `defaultObjectProperties.Layer`)
 * onto a stampable `geometry/create-*` command when the caller did NOT
 * already supply an explicit `layer`. AutoCAD assigns CLAYER to EVERY
 * newly-created entity — that is the ENTIRE practical effect of the LAYER
 * "Set Current" command (SET_ACTIVE_LAYER's AutoCAD counterpart). Before this
 * fix NOTHING read `defaultObjectProperties.Layer` back: every
 * `geometry/create-*` reducer case omitted `layer` from the entity literal it
 * built, so `appendGeometryEntity`'s schema parse silently defaulted every
 * new entity to the hardcoded layer `"0"` — for EVERY caller (interactive
 * drafting, CLI text, AI), not only the AI path the original finding named.
 * `SET_ACTIVE_LAYER` moved the pointer but had ZERO downstream effect.
 *
 * Round-17 P2 (github PR #527 review) — a SPECIAL CASE mirroring
 * `stampCeColorOntoCreateCommand`'s own `convertInsertColor` handling EXACTLY:
 * `geometry/create-block-definition` with `postAction:"convert"` appends a
 * REPLACEMENT INSERT entity (a drawn instance distinct from the colour-less
 * block-definition CONTAINER) — that INSERT inherits CLAYER too, stamped into
 * the dedicated `convertInsertLayer` field (NOT the top-level `layer`, which
 * would incorrectly assign the block DEFINITION itself to CLAYER).
 *
 * Driven from the SAME `editorStore.dispatch` chokepoint as
 * `stampCeColorOntoCreateCommand` / `stampCurrentDimStyleOntoCreateCommand`
 * (nested together, order-independent — each stamp gates on its own field),
 * so the interactive draft, command-line, and AI create paths ALL inherit
 * CLAYER through this one seam.
 *
 * Round-19 P2 (github PR #527 review) — validates `currentLayerName` against
 * the LIVE `doc.styleTables.layers` before stamping (falling back to "0"
 * when it names no live row), instead of trusting the shell pointer blindly.
 * The OPEN/import merge path is a documented dispatch-chokepoint EXCEPTION
 * (CLAUDE.md Priority #2.1 — "специализированный import/bootstrap
 * pipeline") that writes state DIRECTLY via zustand `set`, never through
 * `dispatch()` — so `reconcileCurrentLayerAgainstTable` (which ONLY runs
 * inside the dispatch chokepoint) never gets a chance to catch a CLAYER
 * pointer PRESERVED from the prior document (`syncShellStateFromMerged
 * GeometryDocument`'s own doc comment: "PRESERVES propertyWorkflows") that
 * does not exist in the newly opened/merged one — mirrors the merge-time
 * reconcile DIMSTYLE already has
 * (`reconcileCurrentDimStyleAgainstMergedDocument`). Without this
 * validation, the FIRST create dispatched after such an open/merge would
 * stamp the phantom layer name onto a brand-new entity before the
 * chokepoint's reconcile ever ran.
 */
export function stampCurrentLayerOntoCreateCommand(
  command: EditorCommand,
  currentLayerName: string | undefined,
  doc: DrawingDocument,
): EditorCommand {
  // Round-25 P2 (github PR #527 review) — resolve case-insensitively,
  // mirroring `namesEqualCaseInsensitive` everywhere else a stamped/shell-
  // preserved layer name is matched against the live table (round-21/22/23).
  // A bare `===` here missed the OPEN/import-merge case specifically: the
  // merge path PRESERVES the prior document's CLAYER verbatim
  // (`syncShellStateFromMergedGeometryDocument` — "PRESERVES
  // propertyWorkflows"), so if the prior drawing's CLAYER was "walls" and the
  // NEWLY merged document's table spells the SAME layer "Walls", this exact
  // check treated it as missing and fell back to "0" — even though every
  // other layer lookup in the codebase resolves that pair as one row. Stamp
  // the row's CANONICAL name (not the possibly differently-cased
  // `currentLayerName`) so the created entity's `layer` always matches an
  // ACTUAL table row spelling.
  const matchedLayer =
    currentLayerName !== undefined
      ? (doc.styleTables?.layers ?? []).find((l) =>
          namesEqualCaseInsensitive(l.name, currentLayerName),
        )
      : undefined;
  const resolvedLayerName = matchedLayer?.name ?? "0";
  if (
    command.type === "geometry/create-block-definition" &&
    (command as { postAction?: unknown }).postAction === "convert" &&
    (command as { convertInsertLayer?: unknown }).convertInsertLayer ===
      undefined
  ) {
    return {
      ...command,
      convertInsertLayer: resolvedLayerName,
    } as EditorCommand;
  }
  if (!CURRENT_LAYER_STAMPABLE_CREATE.has(command.type)) return command;
  if ((command as { layer?: unknown }).layer !== undefined) return command;
  return { ...command, layer: resolvedLayerName } as EditorCommand;
}

/**
 * Build the style-table + layout-plot-settings action slice. Receives the host
 * store's `get` (zustand passes `getState` into the initializer — the SAME
 * function as `useEditorStore.getState`).
 */
export function createStyleTableActionsSlice(
  get: StyleTablesGet,
): StyleTableActionsSlice {
  return {
    // Lane J — route through dispatch so the DIMSTYLE edit is undo-able. The
    // reducer suppresses the same-values no-op (no history entry, no bump), but
    // routing it there still runs the whole-document assemble+resync — @codex
    // round-2 P2: short-circuit a same-value Apply BEFORE dispatch so an idle
    // DimStyle dialog re-submit stays free (pre-Lane-J direct-set parity).
    updateDimensionStyle: (styleName, updates) => {
      if (
        isStyleTableUpdateNoOp(
          get().geometryDocument,
          "dimensionStyles",
          styleName,
          updates,
        )
      ) {
        return;
      }
      get().dispatch({
        type: "header/update-dimension-style",
        source: "system",
        styleName,
        updates,
      });
    },
    addDimensionStyle: (name, basedOn = "Standard") => {
      const trimmedName = (name ?? "").trim();
      if (!trimmedName) {
        return { ok: false, error: t("error.dimstyle_name_empty") };
      }
      const store = get();
      const currentStyles =
        store.geometryDocument.styleTables?.dimensionStyles ?? [];
      // @codex #499 P2 — DIMSTYLE table names are CASE-INSENSITIVE (DXF symbol
      // names fold, matching AutoCAD + the sibling `addMultileaderStyle` guard).
      // An EXACT dup check let a user create `STANDARD` alongside the seeded
      // `Standard`; the case-folded delete/rename guards then treated BOTH rows as
      // the protected mandatory style, so the extra one could never be removed.
      // Reject case-variant duplicates UP FRONT so no un-removable twin is born.
      if (
        currentStyles.some(
          (s) => s.name.toLowerCase() === trimmedName.toLowerCase(),
        )
      ) {
        return {
          ok: false,
          error: t("error.dimstyle_already_exists", { name: trimmedName }),
        };
      }
      const baseStyle = resolveBaseStyleEntry(currentStyles, basedOn);
      if (!baseStyle) {
        return { ok: false, error: t("error.dimstyle_no_base") };
      }
      const newStyle: DimensionStyle = {
        ...baseStyle,
        name: trimmedName,
        extraDimVars: { ...(baseStyle.extraDimVars ?? {}) },
        // SL-4 (@codex #425 P2) — a NEW DIMSTYLE record must NOT inherit the source
        // style's own group-105 record handle. Cloning `...baseStyle` copied it, so
        // an imported base (real 105 handle) yielded TWO records sharing one handle.
        // Imported documents BYPASS the conformant remint on save
        // (isImportedSourceDocument → pass-through), so both would serialize at
        // `105 <same-hex>` → duplicate object handle → strict ezdxf/AutoCAD
        // repair-or-reject (data loss). Stripping it lets the save-path remint mint a
        // fresh unique handle on the from-scratch/conformant path; on the imported
        // pass-through a handle-less new record simply cannot collide. In-place edits
        // (updateDimensionStyle / renameDimensionStyle) KEEP the handle — only a
        // brand-NEW record strips it.
        handle: undefined,
      };
      get().dispatch({
        type: "header/add-dimension-style",
        source: "system",
        style: newStyle,
      });
      return { ok: true };
    },
    deleteDimensionStyle: (name) => {
      // Validation guards stay in this wrapper; the actual mutation flows
      // through dispatch → reduceGeometryDocument →
      // applyStyleTablesActionWithMutations so the cache + entity metadata
      // index update via commitMutation in O(|touched|).
      const trimmedName = (name ?? "").trim();
      // @codex P2 — fold case: DIMSTYLE names are case-insensitive, so an imported
      // `STANDARD`/mixed-case mandatory style must stay protected from delete.
      if (trimmedName.toLowerCase() === "standard") {
        return {
          ok: false,
          error: t("error.dimstyle_cannot_delete_standard"),
        };
      }
      const store = get();
      const currentStyles =
        store.geometryDocument.styleTables?.dimensionStyles ?? [];
      if (!currentStyles.some((s) => s.name === trimmedName)) {
        return {
          ok: false,
          error: t("error.dimstyle_not_found", { name: trimmedName }),
        };
      }
      get().dispatch({
        type: "header/delete-dimension-style",
        source: "system",
        styleName: trimmedName,
      });
      // @codex P2 — the CURRENT DIMSTYLE pointer is reconciled AT THE DISPATCH
      // CHOKEPOINT (`reconcileCurrentDimStyleAgainstTable`, run inside this very
      // dispatch): if the deleted style WAS current it falls the pointer back to
      // "Standard" (the SAME target orphaned dimensions get), and the UNDO inverse
      // carries the pre-mutation pointer so undo restores it. Doing it there (not
      // via a separate out-of-band `properties/apply` here) keeps the pointer
      // INSIDE the delete's history entry — so undo/redo never strand it — and
      // covers every caller (dialog, command-line DIMSTYLE, AI) + the redo replay.
      return { ok: true };
    },
    renameDimensionStyle: (oldName, newName) => {
      const trimmedOld = (oldName ?? "").trim();
      const trimmedNew = (newName ?? "").trim();
      // @codex P2 — fold case: DIMSTYLE names are case-insensitive, so an imported
      // `STANDARD`/mixed-case mandatory style must stay protected from rename.
      if (trimmedOld.toLowerCase() === "standard") {
        return {
          ok: false,
          error: t("error.dimstyle_cannot_rename_standard"),
        };
      }
      if (!trimmedNew) {
        return { ok: false, error: t("error.dimstyle_new_name_empty") };
      }
      const store = get();
      const currentStyles =
        store.geometryDocument.styleTables?.dimensionStyles ?? [];
      if (!currentStyles.some((s) => s.name === trimmedOld)) {
        return {
          ok: false,
          error: t("error.dimstyle_not_found", { name: trimmedOld }),
        };
      }
      // @codex #499 P2 — reject renaming TO a CASE-VARIANT of another existing
      // style (DIMSTYLE names fold), else a user could rename `Arch` → `STANDARD`
      // alongside the seeded `Standard` and strand an un-removable case-twin (the
      // delete/rename guards fold case). Exclude the row being renamed so a
      // pure case-fix of the SAME style (`Arch` → `ARCH`) is still allowed.
      if (
        currentStyles.some(
          (s) =>
            s.name !== trimmedOld &&
            s.name.toLowerCase() === trimmedNew.toLowerCase(),
        )
      ) {
        return {
          ok: false,
          error: t("error.dimstyle_already_exists", { name: trimmedNew }),
        };
      }
      get().dispatch({
        type: "header/rename-dimension-style",
        source: "system",
        oldName: trimmedOld,
        newName: trimmedNew,
      });
      // @codex P2 — the CURRENT DIMSTYLE pointer is reconciled AT THE DISPATCH
      // CHOKEPOINT (`reconcileCurrentDimStyleAgainstTable`, run inside this very
      // dispatch): if the renamed style WAS current it re-points old→new, and the
      // UNDO inverse carries the pre-mutation pointer so undoing the rename
      // restores it in lockstep with the table (the reducer already re-points
      // existing dimensions). Doing it there (not via a separate out-of-band
      // `properties/apply` here) keeps the pointer INSIDE the rename's history
      // entry — so undo/redo never strand it at the now-missing new name — and
      // covers every caller (dialog, command-line DIMSTYLE, AI) + the redo replay.
      return { ok: true };
    },
    setCurrentDimensionStyle: (name) => {
      const trimmedName = (name ?? "").trim();
      // @codex P2 — resolve CASE-INSENSITIVELY to the canonical table spelling
      // (DIMSTYLE names are case-insensitive; imported drawings may store
      // `STANDARD`/mixed case). `get().geometryDocument` is passed as an ARGUMENT
      // (not a `const X = …geometryDocument` capture site).
      const canonicalName = resolveDimensionStyleName(
        get().geometryDocument,
        trimmedName,
      );
      if (canonicalName === undefined) {
        return {
          ok: false,
          error: t("error.dimstyle_not_found", { name: trimmedName }),
        };
      }
      // Persist the CANONICAL name as a default object property via the property
      // reducer's no-selection branch (entityIds:[] → merge into
      // defaultObjectProperties). Storing the canonical spelling (not the caller's
      // casing) keeps the manager marker + the new-dimension stamp + the entity
      // remap — all exact-name compares — in lockstep with the table. No new
      // command variant needed — this is the same path CLAYER / current colour
      // default-apply uses, so it stays inside the Command Engine. It is a
      // current-pointer change (not a geometry/table edit): no undo entry, no redo
      // clear (see the interface doc), matching AutoCAD's DIMSTYLE sysvar.
      get().dispatch({
        type: "properties/apply",
        source: "system",
        entityIds: [],
        values: { "Dimension style": canonicalName },
      });
      return { ok: true };
    },
    // Lane J — route through dispatch (undo-able MLEADERSTYLE edit).
    // @codex round-2 P2 — same-value pre-dispatch guard (see updateDimensionStyle).
    updateMultileaderStyle: (styleName, updates) => {
      if (
        isStyleTableUpdateNoOp(
          get().geometryDocument,
          "multileaderStyles",
          styleName,
          updates,
        )
      ) {
        return;
      }
      get().dispatch({
        type: "header/update-multileader-style",
        source: "system",
        styleName,
        updates,
      });
    },
    addMultileaderStyle: (name, basedOn = "Standard") => {
      const trimmedName = (name ?? "").trim();
      if (!trimmedName) {
        return { ok: false, error: t("error.mleaderstyle_name_empty") };
      }
      const store = get();
      const currentStyles =
        store.geometryDocument.styleTables?.multileaderStyles ?? [];
      if (
        currentStyles.some(
          (s) => s.name.toLowerCase() === trimmedName.toLowerCase(),
        )
      ) {
        return {
          ok: false,
          error: t("error.mleaderstyle_already_exists", { name: trimmedName }),
        };
      }
      const baseStyle = resolveBaseStyleEntry(currentStyles, basedOn);
      if (!baseStyle) {
        return {
          ok: false,
          error: t("error.mleaderstyle_no_base"),
        };
      }
      const newStyle: MultileaderStyle = {
        ...baseStyle,
        name: trimmedName,
        extraMlVars: { ...(baseStyle.extraMlVars ?? {}) },
        // SL-5 F2 (@codex round-5) — a NEW MLEADERSTYLE record must NOT inherit
        // the source style's imported group-5 handle (`sourceHandle`). The UI
        // style-copy spreads `...baseStyle`, so an imported base (real source
        // handle) would make the copy claim the ORIGINAL object's handle on
        // export → whichever record a dictionary / reactor ref points at is
        // hijacked → the wrong style after save/reopen (same class as the shipped
        // SL-4 addDimensionStyle 105-handle strip). Stripping it makes the copy a
        // genuinely-new style that mints a FRESH handle on export. In-place edits
        // (`updateMultileaderStyle`, which merges partial updates into the
        // existing record) KEEP the handle — only a brand-NEW record-from-copy
        // strips it.
        sourceHandle: undefined,
      };
      get().dispatch({
        type: "header/add-multileader-style",
        source: "system",
        style: newStyle,
      });
      return { ok: true };
    },
    deleteMultileaderStyle: (name) => {
      // See deleteDimensionStyle above for the dispatch flow.
      const trimmedName = (name ?? "").trim();
      if (trimmedName === "Standard") {
        return {
          ok: false,
          error: t("error.mleaderstyle_cannot_delete_standard"),
        };
      }
      const store = get();
      const currentStyles =
        store.geometryDocument.styleTables?.multileaderStyles ?? [];
      if (!currentStyles.some((s) => s.name === trimmedName)) {
        return {
          ok: false,
          error: t("error.mleaderstyle_not_found", { name: trimmedName }),
        };
      }
      get().dispatch({
        type: "header/delete-multileader-style",
        source: "system",
        styleName: trimmedName,
      });
      return { ok: true };
    },
    // Lane J — route through dispatch (undo-able text-style edit). Text style is
    // referenced by name in Text/MText previews and resolved at render time.
    // @codex round-2 P2 — same-value pre-dispatch guard (see updateDimensionStyle).
    updateTextStyle: (styleName, updates) => {
      if (
        isStyleTableUpdateNoOp(
          get().geometryDocument,
          "textStyles",
          styleName,
          updates,
        )
      ) {
        return;
      }
      get().dispatch({
        type: "header/update-text-style",
        source: "system",
        styleName,
        updates,
      });
    },
    addLayer: (name) => {
      const trimmedName = (name ?? "").trim();
      if (!trimmedName) {
        return { ok: false, error: t("error.layer_name_empty") };
      }
      const store = get();
      const currentLayers = store.geometryDocument.styleTables?.layers ?? [];
      // Coordinator sweep (github PR #527, GraphQL live-finding pass) — case-
      // insensitive duplicate check, mirroring `appendStyleTableEntry`'s OWN
      // `namesEqualCaseInsensitive` guard (round-21) exactly. A bare `===`
      // here let this wrapper DISAGREE with the reducer it dispatches to:
      // adding "walls" while "Walls" already exists passed this check, the
      // wrapper reported `{ ok: true }` and dispatched, but the reducer's
      // case-insensitive guard then silently returned NO_MUTATIONS — the
      // Layer Manager believed the add succeeded and selected/started
      // editing a layer that was never created.
      if (currentLayers.some((l) => namesEqualCaseInsensitive(l.name, trimmedName))) {
        return {
          ok: false,
          error: t("layer.name_duplicate"),
        };
      }
      const newLayer: LayerDefinition = {
        name: trimmedName,
        color: 7,
        visible: true,
        frozen: false,
        locked: false,
        linetype: "Continuous",
        lineweight: -3,
        plottable: true,
        description: "",
        flags: 0,
        handle: "",
      };
      get().dispatch({
        type: "header/add-layer",
        source: "system",
        layer: newLayer,
      });
      return { ok: true };
    },
    // Lane J — route through dispatch (undo-able layer edit). Layer attributes
    // (color/visible/frozen/locked/linetype/lineweight) are looked up by name at
    // render time; no entity preview field is baked from the layer table.
    // @codex round-2 P2 — same-value pre-dispatch guard (see updateDimensionStyle).
    updateLayer: (name, updates) => {
      if (
        isStyleTableUpdateNoOp(
          get().geometryDocument,
          "layers",
          name,
          updates,
        )
      ) {
        return;
      }
      get().dispatch({
        type: "header/update-layer",
        source: "system",
        layerName: name,
        updates,
      });
    },
    deleteLayer: (name) => {
      // See deleteDimensionStyle above for the dispatch flow.
      const trimmedName = (name ?? "").trim();
      if (trimmedName === "0") {
        return {
          ok: false,
          error: t("layer.cannot_delete_0"),
        };
      }
      const store = get();
      const currentLayers = store.geometryDocument.styleTables?.layers ?? [];
      if (!currentLayers.some((l) => l.name === trimmedName)) {
        return {
          ok: false,
          error: t("error.layer_not_found", { name: trimmedName }),
        };
      }
      // Reject deletion of the current layer.
      const currentLayerName =
        store.state.propertyWorkflows.defaultObjectProperties.Layer ?? "0";
      if (trimmedName === currentLayerName) {
        return {
          ok: false,
          error: t("layer.cannot_delete_current"),
        };
      }
      get().dispatch({
        type: "header/delete-layer",
        source: "system",
        layerName: trimmedName,
      });
      // GitHub Codex review (PR #534, round 2) P2 — the dispatch above may
      // have been rejected by the fail-closed collab-lock EDIT veto (another
      // collaborator holds `LOCK LAYER <trimmedName>`); the veto branch
      // already left the layer table genuinely unchanged and pushed its own
      // detailed audit error, but until this check this wrapper still
      // unconditionally reported `{ ok: true }` to its caller (the Layer
      // Manager dialog), which then moved its local selection / closed the
      // row as if the delete had actually happened.
      if (wasLastDispatchCollabLockVetoed()) {
        return {
          ok: false,
          error: t("collab.lock.layer_action_vetoed", { name: trimmedName }),
        };
      }
      return { ok: true };
    },
    // T7 (agent-bridge layer-mgmt) — rename, cascading to every entity
    // currently on `oldName`. Mirrors `renameDimensionStyle`'s validation
    // shape (existence + non-collision), NOT `deleteLayer`'s (no
    // current-layer protection needed — renaming the current layer is safe;
    // the reconcile chokepoint keeps CLAYER live either way).
    renameLayer: (oldName, newName) => {
      const trimmedOld = (oldName ?? "").trim();
      const trimmedNew = (newName ?? "").trim();
      if (trimmedOld === "0") {
        return { ok: false, error: t("layer.cannot_rename_0") };
      }
      if (!trimmedNew) {
        return { ok: false, error: t("error.layer_name_empty") };
      }
      const currentLayers = get().geometryDocument.styleTables?.layers ?? [];
      // Local Codex round-22 P2 (github PR #527 review) — case-insensitive
      // existence + collision lookup, mirroring the reducer's OWN
      // `namesEqualCaseInsensitive` logic (round-21) exactly. A bare `===`
      // here let this wrapper DISAGREE with the reducer it dispatches to:
      // (a) an oldName that only case-varies from the live row (e.g.
      // "walls" vs stored "Walls") wrongly reported "not found" even though
      // the reducer would happily match it; (b) a newName that only
      // case-varies from a DIFFERENT existing row (e.g. renaming "Doors" to
      // "walls" while "Walls" exists) passed this wrapper's check but the
      // reducer's OWN case-insensitive collision guard then silently
      // no-ops it — the wrapper reported `{ ok: true }` for a rename that
      // never happened, and the Layer Manager dialog selected a layer name
      // that names no row. Self-exclusion is BY REFERENCE (`existingOldLayer`),
      // not by string equality, so a legitimate pure recase (e.g.
      // "Walls" -> "walls") is never mistaken for colliding with itself.
      const existingOldLayer = currentLayers.find((l) =>
        namesEqualCaseInsensitive(l.name, trimmedOld),
      );
      if (existingOldLayer === undefined) {
        return {
          ok: false,
          error: t("error.layer_not_found", { name: trimmedOld }),
        };
      }
      // Coordinator sweep (github PR #527, GraphQL live-finding pass) —
      // compare against `existingOldLayer.name` (the CANONICAL stored
      // spelling), NOT the raw `trimmedOld` param, mirroring the reducer's
      // OWN self-rename guard fix. A bare `trimmedOld === trimmedNew` here
      // silently no-opped a legitimate case-NORMALIZING rename: table has
      // "Walls", caller passes `renameLayer("walls", "walls")` — both raw
      // strings are equal even though the actual row is "Walls" and the
      // caller wants it recased to "walls" — the wrapper reported success
      // without ever dispatching the change.
      if (existingOldLayer.name === trimmedNew) {
        // True no-op — nothing to dispatch.
        return { ok: true };
      }
      if (
        currentLayers.some(
          (l) =>
            l !== existingOldLayer && namesEqualCaseInsensitive(l.name, trimmedNew),
        )
      ) {
        return { ok: false, error: t("layer.name_duplicate") };
      }
      get().dispatch({
        type: "header/rename-layer",
        source: "system",
        oldName: trimmedOld,
        newName: trimmedNew,
      });
      // GitHub Codex review (PR #534, round 2) P2 — mirrors `deleteLayer`'s
      // same fix immediately above: the dispatch may have been rejected by
      // the fail-closed collab-lock EDIT veto (keyed off `oldName`, the
      // locked identity), which already left the layer table genuinely
      // unchanged. Report the real failure instead of `{ ok: true }`.
      if (wasLastDispatchCollabLockVetoed()) {
        return {
          ok: false,
          error: t("collab.lock.layer_action_vetoed", { name: existingOldLayer.name }),
        };
      }
      return { ok: true };
    },
    // T7 (agent-bridge layer-mgmt) — moved from a stray direct-`set` sibling
    // (was OUTSIDE this slice, bypassing dispatch entirely) into the SAME
    // current-pointer mechanism `setCurrentDimensionStyle` uses. See the
    // interface JSDoc above for the full rationale.
    setCurrentLayer: (name) => {
      const trimmedName = (name ?? "").trim();
      const currentLayers = get().geometryDocument.styleTables?.layers ?? [];
      // Local Codex round-23 P2 (local read-only review) — resolve
      // case-insensitively, mirroring `renameLayer`'s `existingOldLayer`
      // lookup and the reducer's own table semantics (round-21/22). A bare
      // `===` here rejected a perfectly valid layer switch whenever the
      // caller's casing differed from the table's stored spelling (e.g.
      // "walls" against a stored "Walls"), even though every OTHER layer
      // lookup in this codebase now treats that pair as the SAME row.
      const matchedLayer = currentLayers.find((l) =>
        namesEqualCaseInsensitive(l.name, trimmedName),
      );
      if (matchedLayer === undefined) {
        return {
          ok: false,
          error: t("error.layer_not_found", { name: trimmedName }),
        };
      }
      // Dispatch the row's CANONICAL stored name — `defaultObjectProperties
      // .Layer` must always hold the row's TRUE spelling (the invariant
      // `reconcileCurrentLayerAgainstTable` relies on), not whatever casing
      // the caller happened to pass.
      get().dispatch({
        type: "properties/apply",
        source: "system",
        entityIds: [],
        values: { Layer: matchedLayer.name },
      });
      return { ok: true };
    },
    setLayoutPlotSettings: (spaceId, settings) => {
      // Lane J — guard: skip dispatch (preserving the document reference) for the
      // MODEL-tab / unknown-id / same-settings no-op cases
      // (`resolveLayoutPlotSettingsWrite` returns null — ONE truth shared with the
      // reducer). Otherwise route through dispatch so the page-setup edit is
      // undo-able (mirrors the delete/rename siblings).
      if (
        resolveLayoutPlotSettingsWrite(get().geometryDocument, spaceId, settings) ===
        null
      ) {
        return;
      }
      get().dispatch({
        type: "header/set-layout-plot-settings",
        source: "system",
        spaceId,
        settings,
      });
    },
  };
}
